import React, { useEffect, useState } from "react";
import axios from "axios";
import { API, useAuth } from "../auth/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, ShieldCheck, Camera } from "lucide-react";
import InspectionQrHandoff from "../components/InspectionQrHandoff";
import ParkingPhotoCapture from "../components/ParkingPhotoCapture";
import KycModal from "../components/KycModal";
import { formatBookingError } from "../lib/bookingErrors";
import { hasHostedProfilePhoto } from "../lib/profilePhoto";
import ListingImage from "../components/ListingImage";
import { PARKING_FALLBACK } from "../lib/parkingImages";
import { mergeParkingPhotoSync, parkingPhotosComplete, parkingPhotosToUrls } from "../lib/parkingPhotos";
import { PARKING_FACILITIES, PARKING_SPACE_TYPES, buildParkingPayload, validateParkingHours } from "../lib/parkingListingConstants";
import HostTermsCheckbox from "../components/HostTermsCheckbox";

export default function NewParking() {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [kycOpen, setKycOpen] = useState(false);
  const [form, setForm] = useState({
    name: "",
    address: "",
    city: "",
    space_type: "driveway",
    price_per_hour: 2.0,
    max_hours: "",
    open_24_7: false,
    open_time: "08:00",
    close_time: "22:00",
    facilities: [],
    description: "",
    images: [],
    accepted_host_terms: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [parkingPhotos, setParkingPhotos] = useState({});
  const [qrToken, setQrToken] = useState(null);

  const kycOk = user?.kyc_status === "approved";
  const photoOk = hasHostedProfilePhoto(user);

  useEffect(() => {
    if (user && user.kyc_status !== "approved") setKycOpen(true);
  }, [user]);

  const goToProfilePhoto = () => {
    toast.error("Upload a real profile photo before listing");
    navigate("/profile/settings");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (user?.kyc_status !== "approved") {
      setKycOpen(true);
      toast.error("Verify your identity before listing parking");
      return;
    }
    if (!hasHostedProfilePhoto(user)) {
      goToProfilePhoto();
      return;
    }
    if (!parkingPhotosComplete(parkingPhotos)) {
      toast.error("Add at least one photo — use your camera, upload, or scan the QR on your phone");
      return;
    }
    const hoursErr = validateParkingHours(form);
    if (hoursErr) {
      toast.error(hoursErr);
      return;
    }
    if (!form.accepted_host_terms) {
      toast.error("Accept the Host Terms & Conditions before submitting");
      return;
    }
    setSubmitting(true);
    try {
      const images = parkingPhotosToUrls(parkingPhotos);
      const payload = buildParkingPayload(form, images);
      await axios.post(`${API}/parking`, payload, { withCredentials: true });
      toast.success("Parking listing submitted for review");
      navigate("/host?tab=parking");
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 403 && d === "kyc_required") {
        setKycOpen(true);
        toast.error("Verify your identity before listing");
      } else if (e.response?.status === 403 && d === "profile_photo_required") {
        goToProfilePhoto();
      } else if (d === "host_terms_required") {
        toast.error("Accept the Host Terms & Conditions before submitting");
      } else toast.error(formatBookingError(d) || "Failed");
    } finally { setSubmitting(false); }
  };

  const toggleFac = (val) => setForm(f => ({
    ...f, facilities: f.facilities.includes(val) ? f.facilities.filter(x => x !== val) : [...f.facilities, val]
  }));

  return (
    <div className="max-w-[1100px] mx-auto px-6 lg:px-10 py-12" data-testid="new-parking-page">
      <div className="eyebrow mb-2">Host</div>
      <h1 className="zc-headline text-4xl md:text-5xl">List a parking space</h1>
      <p className="text-[var(--zc-text-2)] mt-3">Earn passive income from your driveway, garage, or commercial lot.</p>

      {!kycOk && (
        <div
          className="mt-6 p-4 rounded-lg border border-[var(--zc-border)] flex flex-wrap items-center justify-between gap-3"
          style={{ background: "rgba(245,158,11,0.08)" }}
          data-testid="np-kyc-banner"
        >
          <div className="flex items-start gap-3 text-sm text-[var(--zc-text-2)]">
            <ShieldCheck size={18} className="text-[var(--zc-warn,#f59e0b)] shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-[var(--zc-text)]">Identity verification required</div>
              <div className="mt-1">Hosts must complete KYC before adding parking spaces — same as vehicle listings.</div>
            </div>
          </div>
          <button type="button" onClick={() => setKycOpen(true)} className="px-4 h-10 zc-btn-primary text-sm shrink-0" data-testid="np-kyc-start">
            Verify identity
          </button>
        </div>
      )}

      {kycOk && !photoOk && (
        <div
          className="mt-6 p-4 rounded-lg border border-[var(--zc-border)] flex flex-wrap items-center justify-between gap-3"
          style={{ background: "rgba(245,158,11,0.08)" }}
          data-testid="np-photo-banner"
        >
          <div className="flex items-start gap-3 text-sm text-[var(--zc-text-2)]">
            <Camera size={18} className="text-[var(--zc-warn,#f59e0b)] shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-[var(--zc-text)]">Profile photo required</div>
              <div className="mt-1">Upload a real photo of yourself in profile settings before listing.</div>
            </div>
          </div>
          <button type="button" onClick={goToProfilePhoto} className="px-4 h-10 zc-btn-primary text-sm shrink-0" data-testid="np-photo-settings">
            Add photo
          </button>
        </div>
      )}

      <fieldset disabled={!(kycOk && photoOk)} className={"mt-10 border-0 p-0 m-0 min-w-0 " + (!(kycOk && photoOk) ? "opacity-60" : "")}>
      <form onSubmit={submit} className="grid md:grid-cols-[1fr,360px] gap-6" data-testid="new-parking-form">
        <div className="space-y-6">
          <div className="zc-card p-6">
            <div className="eyebrow mb-4">Parking information</div>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Name or lot # (optional)" testid="np-name" v={form.name} on={v => setForm(f => ({ ...f, name: v }))} placeholder="Downtown Driveway"
                hint="Use a friendly name for a home driveway, or a lot/unit number for buildings" />
              <Field label="Address" testid="np-address" v={form.address} on={v => setForm(f => ({ ...f, address: v }))} required placeholder="150 Biscayne Crescent" />
              <Field label="City" testid="np-city" v={form.city} on={v => setForm(f => ({ ...f, city: v }))} required placeholder="Brampton" />
              <Field label="Price per hour ($)" type="number" testid="np-rate" v={form.price_per_hour} on={v => setForm(f => ({ ...f, price_per_hour: v }))} />
            </div>
            <div className="mt-4">
              <Field label="Max stay per booking (hours)" type="number" testid="np-max-hours" v={form.max_hours} on={v => setForm(f => ({ ...f, max_hours: v }))} placeholder="e.g. 3" hint="Leave blank if renters can stay the full operating window." />
            </div>
          </div>

          <div className="zc-card p-6" data-testid="np-space-type-card">
            <div className="eyebrow mb-2">Space type</div>
            <p className="text-xs text-[var(--zc-text-3)] mb-4">Choose one space type for this listing.</p>
            <div className="flex flex-wrap gap-2" data-testid="np-type">
              {PARKING_SPACE_TYPES.map(([val, lbl]) => (
                <button key={val} type="button" onClick={() => setForm(f => ({ ...f, space_type: val }))}
                  className={"px-3 h-9 text-xs zc-chip " + (form.space_type === val ? "zc-chip-active" : "")}
                  data-testid={`np-type-${val}`}>
                  {lbl}
                </button>
              ))}
            </div>
          </div>

          <div className="zc-card p-6" data-testid="np-amenities-card">
            <div className="eyebrow mb-2">Amenities</div>
            <p className="text-xs text-[var(--zc-text-3)] mb-4">Select all that apply.</p>
            <div className="flex flex-wrap gap-2">
              {PARKING_FACILITIES.map(([val, lbl, icon]) => (
                <button key={val} type="button" onClick={() => toggleFac(val)}
                  className={"px-3 h-9 inline-flex items-center gap-1.5 text-xs zc-chip " + (form.facilities.includes(val) ? "zc-chip-active" : "")}
                  data-testid={`np-fac-${val}`}>
                  {icon} {lbl}
                </button>
              ))}
            </div>
          </div>

          <div className="zc-card p-6">
            <div className="eyebrow mb-3">Hours</div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.open_24_7} onChange={e => setForm(f => ({ ...f, open_24_7: e.target.checked }))} className="w-4 h-4 accent-[var(--zc-accent)]" data-testid="np-247" />
              Open 24/7
            </label>
            {!form.open_24_7 && (
              <div className="grid grid-cols-2 gap-3 mt-4">
                <TimeField label="Open" testid="np-open" v={form.open_time} on={v => setForm(f => ({ ...f, open_time: v }))} />
                <TimeField label="Close" testid="np-close" v={form.close_time} on={v => setForm(f => ({ ...f, close_time: v }))} />
              </div>
            )}
            {!form.open_24_7 && form.open_time && form.close_time && (
              <p className="text-xs text-[var(--zc-text-3)] mt-3">
                Available {form.open_time} – {form.close_time} daily
              </p>
            )}
            {form.open_24_7 && (!form.max_hours || Number(form.max_hours) >= 24) && (
              <p className="text-caption text-[var(--zc-success)] mt-3">24-hour parking available</p>
            )}
            {form.max_hours && Number(form.max_hours) < 24 && (
              <p className="text-xs text-[var(--zc-text-2)] mt-3">Max stay: {form.max_hours} hours per booking</p>
            )}
          </div>

          <div className="zc-card p-6">
            <div className="eyebrow mb-3">Description</div>
            <textarea className="zc-input w-full p-3 text-sm h-28" value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))} data-testid="np-desc"
              placeholder="Tell renters what makes this space convenient..." />
          </div>
        </div>

        <aside className="space-y-6">
          <div className="zc-card p-6" data-testid="np-photos-section">
            <div className="eyebrow mb-1">Photos</div>
            <p className="text-xs text-[var(--zc-text-3)] mb-4">
              Show renters the space — capture on this device or scan the QR to use your phone camera.
            </p>
            <InspectionQrHandoff
              phase="parking_listing"
              parkingLabel={[form.name, form.address, form.city].filter(Boolean).join(" · ") || undefined}
              active
              photos={parkingPhotos}
              onPhotosSync={(incoming) => {
                setParkingPhotos((prev) => {
                  const merged = mergeParkingPhotoSync(prev, incoming);
                  setForm((f) => ({ ...f, images: parkingPhotosToUrls(merged) }));
                  return merged;
                });
              }}
              onToken={setQrToken}
              testid="np-photo-qr"
            />
            <ParkingPhotoCapture
              value={parkingPhotos}
              onChange={(next) => {
                setParkingPhotos(next);
                setForm((f) => ({ ...f, images: parkingPhotosToUrls(next) }));
              }}
              qrToken={qrToken}
              testid="np-photo-capture"
            />
          </div>

          <div className="zc-card p-6" data-testid="np-preview">
            <div className="eyebrow mb-1">Preview</div>
            <p className="text-xs text-[var(--zc-text-3)] mb-4">
              How your listing will appear to renters after approval. Add photos above to see the cover image.
            </p>
            {form.images.length > 0 ? (
              <div className="aspect-[16/10] overflow-hidden rounded-lg bg-[var(--zc-bg-3)]">
                <ListingImage images={form.images} fallback={PARKING_FALLBACK} alt="" className="w-full h-full object-cover" />
              </div>
            ) : (
              <div className="aspect-[16/10] rounded-lg bg-[var(--zc-bg-2)] border border-dashed border-[var(--zc-border)] grid place-items-center text-xs text-[var(--zc-text-3)] px-4 text-center">
                Cover photo appears here once you add at least one parking photo.
              </div>
            )}
            <div className="mt-3">
              <div className="zc-title text-lg">{form.name || "Spot name"}</div>
              <div className="text-xs text-[var(--zc-text-3)] mt-1">{form.address || "Address"}, {form.city || "City"}</div>
              <div className="zc-price text-2xl mt-3">${parseFloat(form.price_per_hour || 0).toFixed(2)}<span className="zc-unit normal-case tracking-normal"> /hr</span></div>
            </div>
          </div>

          <HostTermsCheckbox
            checked={form.accepted_host_terms}
            onChange={(checked) => setForm((f) => ({ ...f, accepted_host_terms: checked }))}
            testid="np-host-terms"
            disabled={submitting}
          />
          <button type="submit" disabled={submitting || !form.accepted_host_terms} className="w-full h-12 zc-btn-primary text-sm flex items-center justify-center gap-2" data-testid="np-submit">
            <Plus size={14} /> {submitting ? "Submitting…" : "Submit for verification"}
          </button>
          <button type="button" onClick={() => navigate(-1)} className="w-full h-11 zc-btn-ghost text-sm" data-testid="np-cancel">
            Discard
          </button>
        </aside>
      </form>
      </fieldset>
      <KycModal
        open={kycOpen}
        onClose={() => setKycOpen(false)}
        onApproved={async () => {
          setKycOpen(false);
          await checkAuth?.();
          toast.success("Identity verified — you can submit your listing");
        }}
      />
    </div>
  );
}

function TimeField({ label, v, on, testid }) {
  return (
    <div>
      <label className="text-xs text-[var(--zc-text-3)]">{label}</label>
      <input
        type="time"
        required
        className="zc-input w-full h-11 px-3 text-sm mt-1"
        value={v}
        onChange={e => on(e.target.value)}
        data-testid={testid}
      />
    </div>
  );
}

function Field({ label, v, on, type = "text", required, placeholder, testid, hint }) {
  return (
    <div>
      <label className="text-xs text-[var(--zc-text-3)]">{label}</label>
      <input type={type} required={required} placeholder={placeholder} className="zc-input w-full h-11 px-3 text-sm mt-1" value={v} onChange={e => on(e.target.value)} data-testid={testid} />
      {hint && <p className="text-xs text-[var(--zc-text-3)] mt-1">{hint}</p>}
    </div>
  );
}
